import {useMessage} from '@coolcolduk/ui-mui';
import {Stack, Typography} from '@mui/material';
import {useEffect} from 'react';
import {useSearchParams} from 'react-router';
import {PaymentMethodCollection, UserAccount} from '../../../__generated__/resolvers-types';
import CenteredText from '../../../components/centeredText/CenteredText';
import {env} from '../../../constants';
import useCreateCheckoutSession from '../../../hook/useCreateCheckoutSession';
import useGetPortalLink from '../../../hook/useGetPortalLink';
import useGetProducts from '../../../hook/useGetProducts';
import useGetUser from '../../../hook/useGetUser';
import {firebaseAuth} from '../../../setup/firebase';
import SubscriptionOption from './SubscriptionOption';
import SubscriptionPlan from './SubscriptionPlan';

export interface SubscriptionProps {
  user: UserAccount | null | undefined;
}

export default function Subscription({user}: SubscriptionProps) {
  const {showMessage} = useMessage();
  const {products, loading, error} = useGetProducts();
  const {createStripeCheckoutSession} = useCreateCheckoutSession();
  const {getPortalLink, error: getPortalLinkError} = useGetPortalLink();
  const [searchParams] = useSearchParams();
  const {refetch} = useGetUser(firebaseAuth);

  const stripeRole = user?.role || env.defaultStripeRole;
  const isReturn = searchParams.get('return');

  useEffect(() => {
    if (!isReturn) return;
    if (!firebaseAuth.currentUser) return;

    const refreshUserData = async () => {
      // Refresh the Firebase token to get updated claims
      await firebaseAuth.currentUser!.getIdToken(true);

      // Refetch user data to get updated subscription information
      await refetch();
    };

    refreshUserData();
  }, [isReturn, refetch]);

  const handleSubscribe = async (priceId: string) => {
    try {
      const {data} = await createStripeCheckoutSession(
        priceId,
        `${window.location.origin}/subscription/success?return=true`,
        `${window.location.origin}/subscription/failed`,
        undefined, // metadata
        true, // allowPromotionCodes - enable promotion codes during checkout
        PaymentMethodCollection.IF_REQUIRED, // paymentMethodCollection - collect payment method only if required
      );

      if (data?.createStripeCheckoutSession?.url) {
        window.location.href = data.createStripeCheckoutSession.url;
      }
    } catch (_err) {
      showMessage('Failed to create checkout session', undefined, 'error');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const {data} = await getPortalLink(`${window.location.href.split('?')[0]}?return=true`);
      if (data?.getStripePortalLink?.url) {
        window.location.href = data.getStripePortalLink.url;
      }

      if (getPortalLinkError) {
        showMessage('Failed to access customer portal', undefined, 'error');
        console.error(getPortalLinkError);
      }
    } catch (_err) {
      showMessage('Failed to access customer portal', undefined, 'error');
    }
  };

  if (!user) {
    return <CenteredText text="Please login to manage your subscription" minHeight={300} />;
  }

  if (loading) {
    return <CenteredText text="Loading products..." minHeight={300} />;
  }

  if (error) {
    return <CenteredText text={`Error loading products: ${error.message}`} minHeight={300} />;
  }

  return (
    <Stack sx={{p: 3}} spacing={3}>
      <Typography variant="h4" gutterBottom>
        {stripeRole === env.defaultStripeRole ? 'Choose Your Plan' : 'Manage Your Plan'}
      </Typography>
      {stripeRole === env.defaultStripeRole ? (
        <SubscriptionOption products={products} handleSubscribe={handleSubscribe} />
      ) : (
        <SubscriptionPlan
          products={products}
          handleManageSubscription={handleManageSubscription}
          stripeRole={stripeRole}
        />
      )}
    </Stack>
  );
}
